/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useState, useLayoutEffect, useRef, useMemo } from 'preact/hooks';
import {
  Font,
  TextDimensions,
  MeasurementBackend,
  getBackend,
  computeBaseStyle,
  getCanvasTextDimensions,
  getSvgTextDimensions,
  getHtmlTextDimensions
} from './textDimensionsUtils';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { useAddBusyState } from '#hooks/PRIVATE_useAddBusyState';

type UseTextDimensionsOptions = {
  backend?: MeasurementBackend | 'auto';
};

export type GetTextDimensionsType = (textString: string, font?: Font) => TextDimensions;

const clientBrowser = getClientHints().browser;

const useTextDimensions = ({ backend = 'auto' }: UseTextDimensionsOptions = {}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const htmlRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const [measureFunction, setMeasureFunction] =
    useState<(textString: string, font?: Font) => TextDimensions>();

  const backendConfig = useMemo(() => {
    const canvasContent = <canvas ref={canvasRef} style={{ display: 'none' }} />;
    const htmlContent = <div ref={htmlRef} />;
    const svgContent = (
      <svg ref={svgRef} style={{ display: 'block', width: '0px', height: '0px' }} />
    );

    const config = {
      canvas: {
        refs: [canvasRef],
        measureContent: canvasContent,
        measureFunction: (textString: string, font?: Font): TextDimensions => {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (!canvas || !ctx) return { x: -1, y: -1, width: -1, height: -1 };
          return getCanvasTextDimensions(ctx, textString, font);
        }
      },
      htmlSvg: {
        refs: [htmlRef, svgRef],
        measureContent: (
          <div>
            {htmlContent}
            {svgContent}
          </div>
        ),
        measureFunction: (textString: string, font?: Font): TextDimensions => {
          if (!htmlRef.current || !svgRef.current) return { x: -1, y: -1, width: -1, height: -1 };
          return getHtmlTextDimensions(
            htmlRef.current,
            { backend: 'svg', container: svgRef.current },
            textString,
            font
          );
        }
      },
      htmlCanvas: {
        refs: [htmlRef, canvasRef],
        measureContent: (
          <div>
            {htmlContent}
            {canvasContent}
          </div>
        ),
        measureFunction: (textString: string, font?: Font): TextDimensions => {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (!htmlRef.current || !canvas || !ctx) return { x: -1, y: -1, width: -1, height: -1 };
          return getHtmlTextDimensions(
            htmlRef.current,
            { backend: 'canvas', ctx },
            textString,
            font
          );
        }
      },
      svg: {
        refs: [svgRef],
        measureContent: svgContent,
        measureFunction: (textString: string, font?: Font): TextDimensions => {
          if (!svgRef.current) return { x: -1, y: -1, width: -1, height: -1 };
          return getSvgTextDimensions(svgRef.current, textString, font);
        }
      }
    };
    if (backend === 'auto') {
      return {
        refs: [htmlRef, canvasRef],
        measureContent: (
          <div>
            {canvasContent}
            {htmlContent}
            {svgContent}
          </div>
        ),
        measureFunction: (textString: string, font?: Font): TextDimensions => {
          const measurementBackend = getBackend(clientBrowser, font);
          return config[measurementBackend].measureFunction(textString, font);
        }
      };
    }
    return config[backend];
  }, [backend]);

  const { onBusyStart, onBusyEnd } = useAddBusyState('useTextDimensions: document fonts');

  useLayoutEffect(() => {
    if (!backendConfig.refs[0].current) return;
    computeBaseStyle(backendConfig.refs[0].current);
    // Ensure all fonts are loaded before any measurements are made
    onBusyStart();
    document.fonts.ready.then(() => {
      setMeasureFunction(() => backendConfig.measureFunction);
      onBusyEnd();
    });
  }, [backendConfig, onBusyStart, onBusyEnd]);

  return {
    textMeasureContent: backendConfig.measureContent,
    getTextDimensions: measureFunction
  };
};

export { useTextDimensions };
